import React from 'react';
import { TEXT_MUTED } from './tokens';

/**
 * Form primitives.
 *
 * One input class string was repeated 41 times across the module and the
 * matching label 41 times with it, split between `mb-1` and `mb-2` for no
 * reason. The checkbox class was another 15 repetitions.
 */

const CONTROL =
  'w-full h-9 px-3 text-sm rounded-lg border border-gray-200 dark:border-white/[0.08] ' +
  'bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white ' +
  'placeholder:text-gray-400 dark:placeholder:text-gray-500 ' +
  'focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

/** Label + optional hint/error wrapper. */
const Field = ({ label, hint, error, required, children, className = '' }) => (
  <div className={className}>
    {label && (
      <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="mt-1 text-[12px] text-red-500">{error}</p>
    ) : (
      hint && <p className={`mt-1 text-[12px] ${TEXT_MUTED}`}>{hint}</p>
    )}
  </div>
);

export const TextInput = ({ className = '', ...props }) => (
  <input className={`${CONTROL} ${className}`} {...props} />
);

export const Select = ({ className = '', children, ...props }) => (
  <select className={`${CONTROL} ${className}`} {...props}>
    {children}
  </select>
);

export const Textarea = ({ className = '', rows = 3, ...props }) => (
  <textarea className={`${CONTROL} h-auto py-2 ${className}`} rows={rows} {...props} />
);

/**
 * Checkbox with its label as one target.
 *
 * The originals put the input and text side by side without a shared label
 * element, so only the 16px box was clickable.
 */
export const Checkbox = ({ label, description, checked, onChange, disabled, ...props }) => (
  <label
    className={`flex items-start gap-3 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={onChange}
      disabled={disabled}
      className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-2 focus:ring-orange-500/40 bg-white dark:bg-[#1e1e1e]"
      {...props}
    />
    <span className="min-w-0">
      <span className="block text-sm text-gray-800 dark:text-gray-200">{label}</span>
      {description && <span className={`block text-[12px] mt-0.5 ${TEXT_MUTED}`}>{description}</span>}
    </span>
  </label>
);

/** Toggle switch, for settings rows where a checkbox reads as too minor. */
export const Toggle = ({ label, description, checked, onChange, disabled }) => (
  <label className={`flex items-center justify-between gap-4 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
    <span className="min-w-0">
      <span className="block text-sm text-gray-800 dark:text-gray-200">{label}</span>
      {description && <span className={`block text-[12px] mt-0.5 ${TEXT_MUTED}`}>{description}</span>}
    </span>
    <span className="relative flex-shrink-0">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only peer"
      />
      <span className="block w-9 h-5 rounded-full bg-gray-200 dark:bg-white/[0.12] peer-checked:bg-orange-600 transition-colors" />
      <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
    </span>
  </label>
);

export default Field;
